﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceViewAction } from "../Store";
import { SelfServiceRegistrationPage } from "../Types";
import type { SelfServiceViewState } from "../Types";
import Button from "~base/Components/Primitives/Button";
import classNames from "classnames";
import Expander from "~base/Components/Layout/Expander";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface FieldErrorProps {
    message?: string;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    fieldErrorContainer: {
        marginLeft: "8px",
        marginRight: "8px",
        marginTop: "-20px",
        marginBottom: "15px",

        opacity: 1,

        transition: "opacity 500ms ease",
    },
    fieldErrorContainerHidden: {
        opacity: 0,
    },
    fieldErrorText: {
        color: "#ffffff",
        fontFamily: theme.constants.defaultFont,
        fontSize: "15px",
        border: "1px solid #ff0000",
        borderTop: "none",
        borderBottomLeftRadius: "5px",
        borderBottomRightRadius: "5px",
        background: "#d95656",
        padding: "5px 10px",

        "& a": {
            color: "#ffffff",
            fontWeight: 600,

            "&:hover": {
                color: "#000000",
            },
        },
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const FieldError: React.FunctionComponent<FieldErrorProps> = ({ message }) => {
    const intl = useIntl();
    const classes = useStyles();

    const [lastMessage, setLastMessage] = React.useState(message);

    React.useEffect(() => {
        if (!!message) {
            setLastMessage(message);
        }
    }, [message, setLastMessage]);

    return (
        <div
            className={classNames(
                classes.fieldErrorContainer,
                !message && classes.fieldErrorContainerHidden
            )}
        >
            <Expander testIdentifier="FieldError" expanded={!!message}>
                <div
                    className={classes.fieldErrorText}
                    dangerouslySetInnerHTML={{ __html: lastMessage }}
                />
            </Expander>
        </div>
    );
};

export default FieldError;
